import React from 'react';

import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import TextField from "@mui/material/TextField";
import IconButton from '@mui/material/IconButton';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { RemoveShoppingCart, Share as ShareIcon } from "@mui/icons-material";
import HomeIcon from '@mui/icons-material/Home';
import SaveIcon from '@mui/icons-material/Save';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import { CartContext } from '../../cart/CartHandler';
import PrestaGlobals from "../../common/PrestaGlobals";

interface UnifiedInstacartHeaderProps {
    onCollapseToggle?: () => void;
    isCollapsed?: boolean;
    onHandleDisabledChange?: (disabled: boolean) => void;
}

const UnifiedInstacartHeader: React.FC<UnifiedInstacartHeaderProps> = (props) => {
    const disableHandle  = () => props.onHandleDisabledChange?.(true);
    const restoreHandle  = () => props.onHandleDisabledChange?.(false);
    const theme = useTheme();
    const cartContext = React.useContext(CartContext);
    const [clearDialogOpen, setClearDialogOpen] = React.useState<boolean>(false);
    const [shareDialogOpen, setShareDialogOpen] = React.useState<boolean>(false);
    const [shareUrl, setShareUrl] = React.useState<string>('');
    const [shareLoading, setShareLoading] = React.useState<boolean>(false);

    return (
        <>
            <AppBar
                position="relative"
                sx={{
                    zIndex: 100,
                    boxShadow: "0px 0px 20px rgba(0,0,0,0.25)",
                }}
            >
                <Toolbar
                    variant="dense"
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    {/* Left: label + input */}
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{ flex: '0 0 auto' }}
                        onMouseEnter={disableHandle}
                        onMouseLeave={restoreHandle}
                    >
                        <Tooltip title="Mentés és főoldal">
                            <IconButton
                                sx={{ p: 0, m: 0 }}
                                onClick={() => cartContext.saveCart().then(() => { location.href = '/'; })}
                            >
                                <HomeIcon />
                            </IconButton>
                        </Tooltip>
                        <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{
                                color: theme.palette.secondary.main,
                                fontWeight: 'bold',
                                display: { xs: 'none', md: 'block' },
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Projekt neve:
                        </Typography>
                        <TextField
                            size="small"
                            value={cartContext.cartName}
                            onChange={(e) => cartContext.updateCartName(e.target.value)}
                            onBlur={() => cartContext.saveCartName()}
                            disabled={false}
                            multiline={false}
                            sx={{
                                width: { xs: '130px', sm: '300px' },
                                color: theme.palette.secondary.main,
                                backgroundColor: `${theme.palette.primary.light} !important`,
                                borderRadius: '4px',
                                border: `1px solid ${theme.palette.secondary.main}`,
                                '& .MuiOutlinedInput-root': {
                                    height: 32,
                                },
                                '& .MuiInputBase-input': {
                                    padding: '2px 8px',
                                    fontSize: '0.75rem',
                                },
                            }}
                        />
                        <Tooltip title="Kosár mentése">
                            <IconButton sx={{ p: 0, m: 0 }} onClick={() => cartContext.saveCart()}>
                                <SaveIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {/* Middle: explicit drag zone — pointer events pass through to PanelResizeHandle */}
                    <Box
                        sx={{
                            flex: '1 1 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'row-resize',
                            height: '100%',
                            opacity: 0.35,
                        }}
                    >
                        <DragHandleIcon />
                    </Box>

                    {/* Right: action buttons */}
                    <Stack
                        direction="row"
                        gap={2}
                        justifyContent="flex-end"
                        alignItems="center"
                        sx={{ flex: '0 0 auto' }}
                        onMouseEnter={disableHandle}
                        onMouseLeave={restoreHandle}
                    >
                        <Tooltip title={PrestaGlobals.isLoggedIn ? "Becsült ár újraszámolása" : "Jelentkezzen be a számoláshoz"}>
                            <span>
                                <IconButton
                                    sx={{ p: 0, m: 0, opacity: PrestaGlobals.isLoggedIn ? 1 : 0.5 }}
                                    disabled={cartContext.opDataRealState}
                                    onClick={() => (!PrestaGlobals.isLoggedIn ? location.href = PrestaGlobals.loginBackURL : cartContext.recalcCartItems())}
                                >
                                    <CalculateIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Kosár ürítése">
                            <IconButton sx={{ p: 0, m: 0 }}>
                                <RemoveShoppingCart onClick={() => setClearDialogOpen(true)} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Kosár megosztása">
                            <IconButton
                                sx={{ p: 0, m: 0 }}
                                onClick={async () => {
                                    setShareUrl('');
                                    setShareLoading(true);
                                    setShareDialogOpen(true);
                                    const url = await cartContext.generateShareLink();
                                    setShareUrl(url);
                                    setShareLoading(false);
                                }}
                            >
                                <ShareIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={cartContext.hasHardwareError() ? "Vasalat ára nem elérhető" : "Rendelés leadása"}>
                            <Box onClick={() => cartContext.checkout()}>
                                <IconButton sx={{ p: 0, m: 0 }} disabled={cartContext.hasHardwareError()}>
                                    <img
                                        src={'/modules/ubercabinet/views/images/instacart-icons/place-order.svg'}
                                        alt="Rendelés leadása"
                                        style={{ marginRight: "0.5em", color: theme.palette.secondary.main }}
                                    />
                                    <Typography
                                        sx={{
                                            color: theme.palette.secondary.main,
                                            fontWeight: 'bold',
                                            display: { xs: 'none', md: 'block' },
                                        }}
                                    >
                                        Rendelés leadása
                                    </Typography>
                                </IconButton>
                            </Box>
                        </Tooltip>
                        {props.onCollapseToggle && (
                            <Tooltip title={props.isCollapsed ? "Kosár megnyitása" : "Kosár bezárása"}>
                                <IconButton sx={{ p: 0, m: 0 }} onClick={props.onCollapseToggle}>
                                    {props.isCollapsed ?  <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Toolbar>

                <Toolbar
                    variant="dense"
                    sx={{ backgroundColor: theme.palette.primary.light }}
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="center"
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ flexGrow: 1 }}
                    >
                        <Typography variant="subtitle1" component="div">
                            {cartContext.getTotalItemCount()} db termék
                        </Typography>
                        <Typography variant="subtitle1" component="div">
                            Összesen: {cartContext.getTotalPrice()} Ft
                        </Typography>
                        <Typography variant="subtitle1" component="div">
                            {cartContext.getOpData() ? (
                                cartContext.opDataRealState
                                    ? <> + maradék értéke: {JSON.stringify(cartContext.getOpData())} Ft {cartContext.opDataRealFlag ? <CheckCircleIcon fontSize="inherit" /> : <WarningAmberIcon fontSize="inherit" sx={{ color: 'warning.main' }} />}</>
                                    : ` + becsült maradék értéke: ${JSON.stringify(cartContext.getOpData())} Ft`
                            ) : ''}
                        </Typography>
                        <Typography variant="subtitle1" component="div">
                            Teljes ár: {cartContext.getTotalPrice() + cartContext.getOpData()} Ft
                        </Typography>
                        {cartContext.hasHardwareError() && (
                            <Typography variant="subtitle1" component="div" sx={{ color: 'error.main' }}>
                                <WarningAmberIcon fontSize="inherit" /> Vasalat ára nem elérhető
                            </Typography>
                        )}
                    </Stack>
                </Toolbar>
            </AppBar>

            <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
                <DialogTitle>Kosár megosztása</DialogTitle>
                <DialogContent>
                    <DialogContentText>Másold ki a lenti linket és oszd meg!</DialogContentText>
                    <TextField
                        fullWidth
                        value={shareUrl}
                        placeholder={shareLoading ? 'Generálás...' : ''}
                        InputProps={{ readOnly: true }}
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        sx={{ color: theme.palette.primary.contrastText, backgroundColor: theme.palette.success.main }}
                        disabled={shareLoading || !shareUrl}
                        onClick={() => navigator.clipboard.writeText(shareUrl)}
                    >
                        Másolás
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ color: theme.palette.primary.contrastText, backgroundColor: theme.palette.success.main }}
                        onClick={() => setShareDialogOpen(false)}
                    >
                        Bezárás
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
                <DialogTitle>Kosár ürítése</DialogTitle>
                <DialogContent>
                    <DialogContentText>Biztosan törölni szeretnéd az összes elemet a kosárból?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setClearDialogOpen(false)}
                        variant="contained"
                        sx={{ color: theme.palette.primary.contrastText, backgroundColor: theme.palette.success.main }}
                    >
                        Mégse
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ color: theme.palette.primary.contrastText, backgroundColor: theme.palette.success.main }}
                        onClick={() => {
                            cartContext.clearCart();
                            setClearDialogOpen(false);
                        }}
                    >
                        Törlés
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UnifiedInstacartHeader;